/**
 * M6.5-W11 — focus-model (centrale keymap-manager).
 *
 * Eén globale `scope`-keuze bepaalt welk paneel/canvas Tab/Arrow-
 * navigatie ontvangt. Switch via:
 *
 * - **F7** — explicit scope-picker popup.
 * - **Muisklik** in een paneel of op canvas — niet-sticky: scope volgt
 *   waar de gebruiker net heeft geklikt. Intuïtief gedrag, voorkomt
 *   "ik klikte op Explorer maar mijn Tab gaat nog steeds door canvas".
 *
 * `focusedCanvasId` is alleen relevant in scope `"canvas"` — daar zit
 * een aparte focus-ring (dashed blauw) bovenop de selectie-ring
 * (solid blauw + glow). De twee zijn losgekoppeld zodat Tab je over
 * nodes laat lopen zonder bij elke stap iets te selecteren; Enter zet
 * selectie op de focused node.
 *
 * Panels regelen hun eigen focus-state intern (DOM-focus via tabIndex);
 * deze manager houdt alleen welke scope er actief is.
 */
import { create } from "zustand";

export type FocusScope =
  | "canvas"
  | "explorer"
  | "diagrams-panel"
  | "type-palette"
  | "relations-panel"
  | "text-pane"
  | "attribute-panel"
  | "toolbar"
  | null;

/**
 * Display-namen + numerieke shortkey voor de scope-picker. De cijfers
 * 1..8 fungeren als directe sprongen binnen de popup (eerste-letter
 * werkt ook maar is ambigue voor "Diagrammen" vs "Relaties"-paneel).
 */
export const SCOPE_LABELS: Array<{ scope: NonNullable<FocusScope>; nl: string; en: string }> = [
  { scope: "canvas", nl: "Canvas", en: "Canvas" },
  { scope: "explorer", nl: "Explorer", en: "Explorer" },
  { scope: "diagrams-panel", nl: "Diagrammen-paneel", en: "Diagrams panel" },
  { scope: "type-palette", nl: "Type-palette", en: "Type palette" },
  { scope: "relations-panel", nl: "Relaties-paneel", en: "Relations panel" },
  { scope: "text-pane", nl: "Onderwater venster", en: "Text pane" },
  { scope: "attribute-panel", nl: "Attribuut-paneel", en: "Attribute panel" },
  { scope: "toolbar", nl: "Canvas-toolbar", en: "Canvas toolbar" },
];

/** Vierkante cel-grootte (px in screen-coords) van de canvas-focus-cursor.
 *  In flow-coords wordt dit gedeeld door de zoom-level — bij inzoomen
 *  blijft de cel visueel ongeveer even groot op het scherm. Klein
 *  genoeg om twee naast-elkaar liggende items te onderscheiden; bij
 *  meerdere items in dezelfde cel zoomt de gebruiker handmatig in. */
export const CANVAS_CURSOR_CELL_SIZE = 40;

interface KeymapState {
  /** Actieve scope; `null` = geen scope (Tab doet de browser-default). */
  scope: FocusScope;
  /**
   * In-focus canvas-id wanneer scope = "canvas". Voor concept-instances
   * is dit de instance-id (viewState-key), voor edges de relation-id.
   * Cleared wanneer scope wisselt of expliciet via `setCanvasFocus(null)`.
   */
  focusedCanvasId: string | null;
  /**
   * Cursor-cel-positie in flow-coords (top-left van de cel). Arrow-
   * toetsen schuiven met `CANVAS_CURSOR_CELL_SIZE`; Tab cyclet door
   * items binnen de huidige cel. `null` = geen cursor (nog niet
   * geïnitialiseerd, of scope ≠ canvas).
   */
  canvasCursor: { x: number; y: number } | null;
  /** Popup-state voor F7-scope-picker. */
  scopePickerOpen: boolean;

  setScope: (scope: FocusScope) => void;
  setCanvasFocus: (id: string | null) => void;
  setCanvasCursor: (pos: { x: number; y: number } | null) => void;
  openScopePicker: () => void;
  closeScopePicker: () => void;
}

export const useKeymap = create<KeymapState>((set, get) => ({
  scope: null,
  focusedCanvasId: null,
  canvasCursor: null,
  scopePickerOpen: false,
  setScope: (scope) => {
    // Bij scope-verandering canvas-focus + cursor altijd resetten —
    // beide hebben alleen betekenis binnen de canvas-scope. Bij
    // re-entry naar "canvas" initialiseert Canvas.tsx ze opnieuw op
    // de eerste focusable's positie.
    if (scope !== get().scope) {
      set({ scope, focusedCanvasId: null, canvasCursor: null });
    }
  },
  setCanvasFocus: (id) => {
    if (get().scope !== "canvas") {
      set({ scope: "canvas", focusedCanvasId: id });
      return;
    }
    set({ focusedCanvasId: id });
  },
  setCanvasCursor: (pos) => {
    if (get().scope !== "canvas") {
      set({ scope: "canvas", canvasCursor: pos });
      return;
    }
    set({ canvasCursor: pos });
  },
  openScopePicker: () => set({ scopePickerOpen: true }),
  closeScopePicker: () => set({ scopePickerOpen: false }),
}));
